// CreatorFeed.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbay.com/APIControllers';

const CreatorFeed = ({ navigation }) => {
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [authMap, setAuthMap] = useState({}); // { [creator]: true|false }
  const [loadingAuth, setLoadingAuth] = useState(false);
const [unlockAll, setUnlockAll] = useState(false);


const [feedMode, setFeedMode] = useState('all'); // 'all' | 'subscribed'


  const fetchPosts = useCallback(async () => {
    try {
      setLoadingPosts(true);
      const res = await fetch(`${API_BASE}/FetchFeedPosts.php`, {
        method: 'GET',
        credentials: 'include',
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.posts)) {
        setPosts(json.posts);
      } else {
        setPosts([]);
      }
    } catch (err) {
      console.error('Error fetching posts', err);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  }, []);


  const fetchAuthForCreators = useCallback(
  async (creators) => {
    if (!creators.length) return;
    setLoadingAuth(true);
    const updated = { ...authMap };
    let anyAuthed = false;

    for (const creator of creators) {
      try {
        const res = await fetch(`${API_BASE}/CheckPublicUserAuth.php`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ Creator: creator }),
        });
        const json = await res.json();
        const isAuthed = !!json.authenticated;

        updated[creator] = isAuthed;

        if (isAuthed) {
          anyAuthed = true; // ✅ ONE CREATOR UNLOCKS ALL
        }
      } catch (err) {
        updated[creator] = false;
      }
    }

    if (anyAuthed) {
      setUnlockAll(true); // ✅ GLOBAL UNLOCK
    }

    setAuthMap(updated);
    setLoadingAuth(false);
  },
  [authMap]
);


  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    // When posts load, fetch auth for unique creators that we don't know yet
    if (!loadingPosts && posts.length) {
      const creators = [
        ...new Set(posts.map((p) => p.Creator).filter(Boolean)),
      ];
      const unknownCreators = creators.filter(
        (c) => typeof authMap[c] === 'undefined'
      );
      if (unknownCreators.length) {
        fetchAuthForCreators(unknownCreators);
      }
    }
  }, [loadingPosts, posts, authMap, fetchAuthForCreators]);

  const renderHeader = () => (
    <View style={styles.header}>
      <View>
        <Text style={styles.title}>Creator Feed</Text>
        <Text style={styles.subtitle}>
          Unlock behind-the-scenes menus, voucher drops & secret orders.
        </Text>
      </View>
      <View style={styles.headerAvatarRow}>
        <View style={styles.avatarStack}>
          <View style={[styles.smallAvatar, { zIndex: 3 }]}>
            <Text style={styles.avatarInitial}>A</Text>
          </View>
          <View style={[styles.smallAvatar, styles.smallAvatarShifted, { zIndex: 2 }]}>
            <Text style={styles.avatarInitial}>T</Text>
          </View>
          <View style={[styles.smallAvatar, styles.smallAvatarShiftedMore, { zIndex: 1 }]}>
            <Text style={styles.avatarInitial}>M</Text>
          </View>
        </View>
        <View style={styles.dotRow}>
          <View style={styles.onlineDot} />
          <Text style={styles.dotText}>Subscribers are live right now</Text>
        </View>
      </View>
    </View>
  );






  const renderPost = (post) => {
    const {
      PostID,
      Creator,
      Text: text,
      ImageURL,
      VoucherCode,
      PostType,
      DateUploaded,
    } = post;

    const isAuthed = unlockAll || !!authMap[Creator];
    const key = `post-${PostID}-${Creator}`;

    return (
      <View key={key} style={styles.postCardContainer}>
        <View style={styles.postCard}>
          <View style={styles.postHeader}>
            <View style={styles.creatorAvatar}>
              <Text style={styles.creatorInitial}>
                {Creator && Creator.charAt(0).toUpperCase()}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.creatorName}>@{Creator}</Text>
              <Text style={styles.postMeta}>
                {formatRelativeTime(DateUploaded)} · Subscribers-only
              </Text>
            </View>
            <TouchableOpacity style={styles.moreButton}>
              <Ionicons name="ellipsis-horizontal" size={20} color="#9ca3af" />
            </TouchableOpacity>
          </View>

          {/* Actual post body */}
          {PostType === 'text' && renderTextPostBody(text)}
          {PostType === 'media' && renderMediaPostBody(ImageURL, text)}
          {PostType === 'voucher' && renderVoucherPostBody(VoucherCode, text)}
          {PostType === 'copy_order' && renderCopyOrderPostBody(VoucherCode, text)}

          {/* Footer row */}
          <View style={styles.postFooter}>
            <View style={styles.footerLeft}>
              <Ionicons name="heart-outline" size={18} color="#6b7280" />
              <Ionicons
                name="chatbubble-ellipses-outline"
                size={18}
                color="#6b7280"
                style={{ marginLeft: 16 }}
              />
              <Ionicons
                name="paper-plane-outline"
                size={18}
                color="#6b7280"
                style={{ marginLeft: 16 }}
              />
            </View>
            <TouchableOpacity style={styles.saveChip}>
              <Ionicons name="bookmark-outline" size={16} color="#111827" />
            </TouchableOpacity>
          </View>

          {/* Auth overlay */}
          {!isAuthed && renderLockOverlay()}
        </View>
      </View>
    );
  };

  const renderTextPostBody = (text) => (
    <View style={styles.textPostBody}>
      <Text style={styles.textPostText}>{text || 'Exclusive drop for subscribers.'}</Text>
    </View>
  );


  const renderMediaPostBody = (imageURL, text) => {
  const isVideo =
    typeof imageURL === 'string' &&
    (imageURL.endsWith('.mp4') ||
     imageURL.endsWith('.webm') ||
     imageURL.endsWith('.mov'));

  return (
    <View style={styles.mediaContainer}>
      {isVideo ? (
        <video
          src={imageURL}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 18,
          }}
          controls
          playsInline
        />
      ) : imageURL ? (
        <Image
          source={{ uri: imageURL }}
          style={styles.mediaImage}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.mediaImage, styles.mediaPlaceholder]}>
          <Ionicons name="image-outline" size={32} color="#9ca3af" />
          <Text style={styles.mediaPlaceholderText}>Creator media</Text>
        </View>
      )}

      {!!text && <Text style={styles.mediaCaption}>{text}</Text>}
    </View>
  );
};


  const renderVoucherPostBody = (voucherCode, text) => (
    <View style={styles.voucherCard}>
      <View style={styles.voucherHeaderRow}>
        <View style={styles.voucherTag}>
          <Ionicons name="ticket-outline" size={14} color="#1f2933" />
          <Text style={styles.voucherTagText}>Voucher drop</Text>
        </View>
        <Text style={styles.voucherBadge}>LIMITED</Text>
      </View>
      <Text style={styles.voucherMainText}>
        {text || 'Unlock a limited-run voucher from this collab.'}
      </Text>
      <View style={styles.voucherCodeRow}>
        <Text style={styles.voucherCodeLabel}>Code</Text>
        <Text style={styles.voucherCode}>
          {voucherCode || 'COMING-SOON'}
        </Text>
      </View>
      <View style={styles.voucherFinePrintRow}>
        <Text style={styles.voucherFinePrint}>Tap to apply at checkout</Text>
        <View style={styles.voucherPerforationDot} />
      </View>
    </View>
  );

  const renderCopyOrderPostBody = (voucherCode, text) => (
    <View style={styles.receiptCard}>
      <View style={styles.receiptHeader}>
        <Text style={styles.receiptTitle}>Copy my order</Text>
        <Ionicons name="receipt-outline" size={18} color="#111827" />
      </View>
      <Text style={styles.receiptSubtitle}>
        Book the exact same combo I had tonight.
      </Text>

      <View style={styles.receiptRow}>
        <Text style={styles.receiptLabel}>Order ref</Text>
        <Text style={styles.receiptValue}>{voucherCode || 'ORDER-REF-PENDING'}</Text>
      </View>
      <View style={styles.receiptRow}>
        <Text style={styles.receiptLabel}>Notes</Text>
        <Text style={styles.receiptValue}>
          {text || 'Extra crispy, sauce on the side, add fries.'}
        </Text>
      </View>
      <View style={styles.receiptFooterLine} />
      <View style={styles.receiptFooterRow}>
        <Text style={styles.receiptFooterText}>Tap to see venue details</Text>
        <Ionicons name="chevron-forward" size={16} color="#6b7280" />
      </View>
    </View>
  );

  const renderLockOverlay = () => (
    <View style={styles.lockOverlay}>
      <View style={styles.lockOverlayInner}>
        <View style={styles.lockIconCircle}>
          <Ionicons name="lock-closed-outline" size={20} color="#111827" />
        </View>
        <Text style={styles.lockTitle}>Subscribers only</Text>
        <Text style={styles.lockBody}>
          Subscribe to this creator to see the full post, vouchers and orders.
        </Text>
        <TouchableOpacity style={styles.lockButton}>
          <Text style={styles.lockButtonText}>View plans</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  if (loadingPosts) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" />
          <Text style={styles.loadingText}>Loading creator feed…</Text>
        </View>
      </SafeAreaView>
    );
  }



  return (
  <View style={{ flex: 1 }}>
    <SafeAreaView style={styles.safe}>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
      >

        {renderHeader()}


<View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 14 }}>
  <TouchableOpacity
    onPress={() => setFeedMode('all')}
    style={{
      paddingVertical: 8,
      paddingHorizontal: 20,
      borderRadius: 999,
      backgroundColor: feedMode === 'all' ? '#111827' : '#e5e7eb',
      marginRight: 8,
    }}
  >
    <Text style={{ color: feedMode === 'all' ? '#fff' : '#111827', fontWeight: '600' }}>
      Main Feed
    </Text>
  </TouchableOpacity>

  <TouchableOpacity
    onPress={() => setFeedMode('subscribed')}
    style={{
      paddingVertical: 8,
      paddingHorizontal: 20,
      borderRadius: 999,
      backgroundColor: feedMode === 'subscribed' ? '#111827' : '#e5e7eb',
    }}
  >
    <Text style={{ color: feedMode === 'subscribed' ? '#fff' : '#111827', fontWeight: '600' }}>
      Supported Creators
    </Text>
  </TouchableOpacity>
</View>


        {/* small “pill” filter row for vibe */}
        <View style={styles.filterRow}>
          <View style={[styles.filterPill, styles.filterPillActive]}>
            <Text style={[styles.filterText, styles.filterTextActive]}>All</Text>
          </View>
          <View style={styles.filterPill}>
            <Text style={styles.filterText}>Media</Text>
          </View>
          <View style={styles.filterPill}>
            <Text style={styles.filterText}>Vouchers</Text>
          </View>
          <View style={styles.filterPill}>
            <Text style={styles.filterText}>Copy my order</Text>
          </View>
        </View>

{posts
  .filter((post) => {
    if (feedMode === 'all') return true;
    return !!authMap[post.Creator]; // ✅ only creators user is subscribed to
  })
  .map(renderPost)}



      </ScrollView>




<View style={styles.footerNav}>
  <TouchableOpacity onPress={() => navigation.navigate('MyFeed')}>
<Ionicons name="film-outline" size={22} color="black" />
  </TouchableOpacity>

  <TouchableOpacity onPress={() => navigation.navigate('CreatorFeed')}>
    <Ionicons name="home" size={28} color="#111827" />
  </TouchableOpacity>

<TouchableOpacity onPress={() => navigation.navigate('UserRecapPage')}>

    <Ionicons name="person-circle-outline" size={28} color="#111827" />
  </TouchableOpacity>
</View>


    </SafeAreaView>
  </View>

  );
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
};

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  scroll: {
    flex: 1,
  },
scrollContent: {
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 90, // ✅ real space for sticky footer
},
  header: {
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    letterSpacing: -0.02,
    color: '#111827',
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 4,
    maxWidth: 260,
  },
  headerAvatarRow: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  avatarStack: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  smallAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#111827',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#f3f4f6',
  },
  smallAvatarShifted: {
    marginLeft: -10,
    backgroundColor: '#4b5563',
  },
  smallAvatarShiftedMore: {
    marginLeft: -10,
    backgroundColor: '#9ca3af',
  },
  avatarInitial: {
    color: '#f9fafb',
    fontSize: 12,
    fontWeight: '600',
  },
  dotRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#10b981',
    marginRight: 6,
  },
  dotText: {
    fontSize: 11,
    color: '#6b7280',
  },
  filterRow: {
    flexDirection: 'row',
    marginBottom: 12,
    gap: 8,
  },
  filterPill: {
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#e5e7eb',
  },
  filterPillActive: {
    backgroundColor: '#111827',
  },
  filterText: {
    fontSize: 12,
    color: '#4b5563',
    fontWeight: '500',
  },
  filterTextActive: {
    color: '#f9fafb',
  },
  postCardContainer: {
    marginBottom: 16,
  },
  postCard: {
    borderRadius: 20,
    backgroundColor: '#f9fafb',
    padding: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  creatorAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#111827',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  creatorInitial: {
    color: '#f9fafb',
    fontWeight: '600',
  },
  creatorName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  postMeta: {
    fontSize: 11,
    color: '#9ca3af',
  },
  moreButton: {
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  textPostBody: {
    paddingVertical: 10,
  },
  textPostText: {
    fontSize: 16,
    lineHeight: 22,
    color: '#111827',
  },
  mediaContainer: {
    borderRadius: 18,
    overflow: 'hidden',
    backgroundColor: '#111827',
    marginTop: 4,
  },
  mediaImage: {
    width: '100%',
    aspectRatio: 4 / 5,
  },
  mediaPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaPlaceholderText: {
    color: '#e5e7eb',
    fontSize: 12,
    marginTop: 6,
  },
  mediaCaption: {
    paddingHorizontal: 10,
    paddingVertical: 10,
    fontSize: 13,
    color: '#f3f4f6',
  },
  voucherCard: {
    backgroundColor: '#fefce8',
    borderRadius: 16,
    padding: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#eab308',
    marginTop: 6,
  },
  voucherHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  voucherTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#fef9c3',
  },
  voucherTagText: {
    fontSize: 11,
    color: '#1f2933',
    fontWeight: '600',
  },
  voucherBadge: {
    fontSize: 11,
    color: '#b45309',
    fontWeight: '700',
  },
  voucherMainText: {
    fontSize: 13,
    color: '#78350f',
    marginBottom: 10,
  },
  voucherCodeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 4,
  },
  voucherCodeLabel: {
    fontSize: 11,
    color: '#92400e',
  },
  voucherCode: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 1.4,
    color: '#1f2933',
  },
  voucherFinePrintRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  voucherFinePrint: {
    fontSize: 10,
    color: '#92400e',
  },
  voucherPerforationDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#eab308',
  },
  receiptCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e5e7eb',
    marginTop: 6,
  },
  receiptHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  receiptTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  receiptSubtitle: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 8,
  },
  receiptRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  receiptLabel: {
    fontSize: 12,
    color: '#4b5563',
  },
  receiptValue: {
    fontSize: 12,
    color: '#111827',
    maxWidth: '60%',
    textAlign: 'right',
  },
  receiptFooterLine: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: 8,
    borderStyle: 'dashed',
  },
  receiptFooterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  receiptFooterText: {
    fontSize: 11,
    color: '#6b7280',
  },
  postFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  saveChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
  },
  lockOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(17, 24, 39, 0.78)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lockOverlayInner: {
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerNav: {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 60,
  backgroundColor: '#ffffff',
  borderTopWidth: 1,
  borderColor: '#e5e7eb',
  flexDirection: 'row',
  justifyContent: 'space-around',
  alignItems: 'center',
  paddingBottom: 6,
zIndex: 999,
elevation: 20,

},

  lockIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f9fafb',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  lockTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#f9fafb',
    marginBottom: 4,
  },
  lockBody: {
    fontSize: 12,
    color: '#e5e7eb',
    textAlign: 'center',
    marginBottom: 10,
  },
  lockButton: {
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: '#f9fafb',
  },
  lockButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    fontSize: 13,
    color: '#6b7280',
  },
});

export default CreatorFeed;
